// Validação de CPF + CNPJ alfanumérico (IN RFB 2.229/2024) — DR-012.
// Cobertura: CPF 11 dígitos numéricos | CNPJ 14 chars [0-9A-Z] + DV numérico.

const CNPJ_VALID_CHAR = /^[0-9A-Z]$/;

/** Normaliza document removendo máscara e convertendo letras pra maiúscula. */
export function normalizeDocument(input: string): string {
  return input.toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** Converte caractere alfanumérico em valor numérico (A=17, ..., Z=42, 0-9 = 0-9). */
function charToValue(char: string): number {
  const code = char.charCodeAt(0);
  // '0'..'9' = 48..57 → 0..9
  // 'A'..'Z' = 65..90 → 17..42
  return code - 48;
}

/** Valida CPF (11 dígitos numéricos + DV mod11). */
export function isValidCpf(input: string): boolean {
  const cpf = normalizeDocument(input);
  if (cpf.length !== 11) return false;
  if (!/^\d{11}$/.test(cpf)) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false; // todos os dígitos iguais

  const calcDigit = (slice: string, weights: number[]): number => {
    const sum = slice.split('').reduce((acc, d, i) => acc + Number(d) * (weights[i] ?? 0), 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const d1 = calcDigit(cpf.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const d2 = calcDigit(cpf.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);

  return d1 === Number(cpf[9]) && d2 === Number(cpf[10]);
}

/** Valida CNPJ alfanumérico (14 chars: 12 [0-9A-Z] + 2 DV numéricos) — DR-012. */
export function isValidCnpj(input: string): boolean {
  const cnpj = normalizeDocument(input);
  if (cnpj.length !== 14) return false;

  // Últimos 2 caracteres (DV) DEVEM ser numéricos
  if (!/^\d{2}$/.test(cnpj.slice(12))) return false;

  // Primeiros 12 caracteres alfanuméricos
  for (let i = 0; i < 12; i++) {
    if (!CNPJ_VALID_CHAR.test(cnpj[i] ?? '')) return false;
  }

  // Rejeita sequências repetidas (todos iguais — ex: 00000000000000)
  if (new Set(cnpj).size === 1) return false;

  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

  const calcDigit = (slice: string, weights: number[]): number => {
    const sum = slice
      .split('')
      .reduce((acc, char, i) => acc + charToValue(char) * (weights[i] ?? 0), 0);
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const d1 = calcDigit(cnpj.slice(0, 12), weights1);
  const d2 = calcDigit(cnpj.slice(0, 13), weights2);

  return d1 === Number(cnpj[12]) && d2 === Number(cnpj[13]);
}

/** Valida CPF/CNPJ baseado em document_type. */
export function isValidDocument(input: string, type: 'CPF' | 'CNPJ'): boolean {
  if (type === 'CPF') return isValidCpf(input);
  if (type === 'CNPJ') return isValidCnpj(input);
  return false;
}

/** Detecta automaticamente CPF vs CNPJ pelo tamanho. */
export function detectDocumentType(input: string): 'CPF' | 'CNPJ' | null {
  const normalized = normalizeDocument(input);
  if (normalized.length === 11) return 'CPF';
  if (normalized.length === 14) return 'CNPJ';
  return null;
}

/** Auto-valida (detecta tipo + valida). */
export function isValidCpfOrCnpj(input: string): boolean {
  const type = detectDocumentType(input);
  if (!type) return false;
  return isValidDocument(input, type);
}

/** Formata CPF com máscara visual XXX.XXX.XXX-XX. */
export function formatCpf(input: string): string {
  const cpf = normalizeDocument(input);
  if (cpf.length !== 11) return input;
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}

/** Formata CNPJ com máscara visual XX.XXX.XXX/XXXX-XX. */
export function formatCnpj(input: string): string {
  const cnpj = normalizeDocument(input);
  if (cnpj.length !== 14) return input;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}

/** Formata document auto-detectando tipo. */
export function formatDocument(input: string): string {
  const type = detectDocumentType(input);
  if (type === 'CPF') return formatCpf(input);
  if (type === 'CNPJ') return formatCnpj(input);
  return input;
}
